package com.oasisfeng.invaluable.identity.store

/**
 * Resilient browser key–value storage.
 *
 * `localStorage` is the natural home for a browser node's identity and world, but it is not always usable: a page
 * opened via `file://` in Safari throws `SecurityError` on *any* `localStorage` access — the object exists, so a
 * `typeof` guard does not catch it — and private-browsing modes have similar quirks. [ResilientKvStore] probes its
 * backing store once with a real write/read/remove; if that throws it serves an in-memory map for the rest of the
 * session, so the app still boots and runs instead of crashing on the first access.
 *
 * [BrowserStore] binds that algorithm to the real `localStorage`. The algorithm itself ([ResilientKvStore] over
 * [RawStore]) is platform-free, so it is unit-tested with a fake backend that either works or throws.
 *
 * [persistent] reports which mode is in effect, so callers can warn the user that changes won't survive a reload.
 */
object BrowserStore {

    private val delegate = ResilientKvStore(LocalStorageRaw)

    /** True when a real, writable `localStorage` backs this store; false when running on the in-memory fallback. */
    val persistent: Boolean get() = delegate.persistent

    fun getItem(key: String): String? = delegate.getItem(key)

    fun setItem(key: String, value: String) = delegate.setItem(key, value)
}

/** The raw, possibly-throwing key–value operations that [ResilientKvStore] guards. */
internal interface RawStore {
    fun get(key: String): String?
    fun set(key: String, value: String)
    fun remove(key: String)
}

/** Probes [raw] once; serves it when it works, else an in-memory map for the rest of the session. */
internal class ResilientKvStore(private val raw: RawStore) {

    private val memory = HashMap<String, String>()

    val persistent: Boolean = runCatching {
        val probeKey = "invaluable.__probe__"
        raw.set(probeKey, "1")
        raw.remove(probeKey)
        true
    }.getOrDefault(false)

    fun getItem(key: String): String? =
        if (persistent) runCatching { raw.get(key) }.getOrElse { memory[key] } else memory[key]

    fun setItem(key: String, value: String) {
        if (persistent && runCatching { raw.set(key, value) }.isSuccess) return
        memory[key] = value
    }
}

/** Binds [RawStore] to the browser's `localStorage`; any access throws on `file://` in Safari (handled by the guard). */
private object LocalStorageRaw : RawStore {
    private fun ls(): dynamic = js("globalThis.window.localStorage")
    override fun get(key: String): String? = ls().getItem(key) as String?
    override fun set(key: String, value: String) { ls().setItem(key, value) }
    override fun remove(key: String) { ls().removeItem(key) }
}
